import { ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { ActionRequestStatus, ActionRequestType } from '@billing/validation';
import { Provider, User } from 'interfaces';
import { formatDateOnly } from 'utils/dateUtils';

const initials = (user?: Partial<User> | Partial<Provider> | null) => {
  const first = (user?.firstName ?? '').trim();
  const last = (user?.lastName ?? '').trim();
  const a = first ? first[0] : '?';
  const b = last ? last[0] : '';
  return (a + b).toUpperCase();
};

type ActionCardProps = {
  title: string;
  reviewer: Partial<User> | Partial<Provider>;
  date: string;
  message: string;
  status: ActionRequestStatus;
  type: ActionRequestType;
  typeLabel: string;
  statusColor: Record<ActionRequestStatus, string>;
  typeColor: Record<ActionRequestType, string>;
  headerRight?: ReactNode;
  bodyRight?: ReactNode;
  testID?: string;
};

export const ActionCard = ({
  title,
  reviewer,
  date,
  message,
  status,
  type,
  typeLabel,
  statusColor,
  typeColor,
  headerRight,
  bodyRight,
  testID,
}: ActionCardProps) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme();

  return (
    <Paper
      radius="lg"
      withBorder
      p="md"
      data-testid={testID}
      style={{
        borderColor:
          mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
        background:
          mode === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
      }}
    >
      <Group align="flex-start" wrap="nowrap">
        <Avatar
          radius="xl"
          size={40}
          color={statusColor[status]}
          variant="outline"
          styles={{ root: { borderWidth: 2 } }}
        >
          {initials(reviewer)}
        </Avatar>
        <Stack gap={8} style={{ flex: 1 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Group gap={8}>
                <Text fw={700} size="sm" data-testid="action-card-title">
                  {title}
                </Text>
                <Badge
                  size="xs"
                  color={typeColor[type]}
                  variant="light"
                  data-testid="action-card-type"
                >
                  {typeLabel}
                </Badge>
                <Badge
                  size="xs"
                  color={statusColor[status]}
                  variant="light"
                  data-testid="action-card-status"
                >
                  {status}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" data-testid="action-card-date">
                {formatDateOnly(date, { fallback: 'N/A' })}
              </Text>
            </Stack>
            {headerRight ?? null}
          </Group>
          <Divider />
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Text
              size="sm"
              style={{ flex: 1 }}
              data-testid="action-card-message"
            >
              {message}
            </Text>
            {bodyRight ?? null}
          </Group>
        </Stack>
      </Group>
    </Paper>
  );
};
